import React, { useMemo } from 'react';

import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { VisitorManagementFeatureProperties } from 'lane-shared/types/features/VisitorManagementFeatureProperties';

import { buildWorkbookConfig } from 'domains/visitorManagement/flatfile/workbook';
import type { FormCustomFieldColumn } from 'domains/visitorManagement/flatfile/workbook';
import { listener } from 'domains/visitorManagement/flatfile/listener';

import styles from 'domains/visitorManagement/content-blocks/AddVisitorsBlock.scss';
import { Space, useFlatfile, useListener, Workbook } from '@flatfile/react';

const EMPTY_FORM_CUSTOM_FIELD_COLUMNS: FormCustomFieldColumn[] = [];

export function VisitorInviteBulkUpload({
  properties,
  handleBulkAdd,
  isPreview = false,
  formCustomFieldColumns = EMPTY_FORM_CUSTOM_FIELD_COLUMNS,
}: {
  properties: VisitorManagementFeatureProperties;
  handleBulkAdd: (visitors: VisitorType[]) => void;
  isPreview?: boolean;
  /** Custom fields on the form; workbook and validation include these as required columns. */
  formCustomFieldColumns?: FormCustomFieldColumn[];
}): React.ReactElement {
  const { t } = useTranslation();
  const { open, openPortal, closePortal } = useFlatfile();

  const workbookConfig = useMemo(
    () => buildWorkbookConfig(formCustomFieldColumns),
    [formCustomFieldColumns]
  );

  const flatfileListener = useMemo(
    () => listener(properties, handleBulkAdd, closePortal, formCustomFieldColumns),
    [properties, handleBulkAdd, closePortal, formCustomFieldColumns]
  );

  useListener(flatfileListener);

  const handleOnClick = () => {
    if (!open) {
      openPortal();
    } else {
      closePortal();
    }
  };

  return (
    <>
      <Button
        onClick={handleOnClick}
        className={styles.bulkUploadButton}
        disabled={isPreview}
        testId="inviteBulkUpload"
      >
        {t('web.content.feature.visitorManagement.visitorInvite.bulkUpload')}
      </Button>
      <Space
        config={{
          name: 'Activate Visitors Space',
          metadata: {
            sidebarConfig: {
              showSidebar: true,
            },
          },
        }}
      >
        {/* Key forces remount when custom columns change so Flatfile sheet shows correct columns */}
        <Workbook
          key={
            formCustomFieldColumns.length > 0
              ? formCustomFieldColumns.map(c => c.fieldKey).join(',')
              : 'default'
          }
          config={workbookConfig}
        />
      </Space>
    </>
  );
}
